import React, { useState, useEffect, useRef } from "react";
import WorkOrderCard from "./TWorkOrderCard";
import { Link } from "react-router-dom";
import "./css/TModalOverlay.css";
import "./css/dropdownTechnician.css";
import "../managerPage/css/dropdown.css";
import Avatar from "../../components/Avatar/Avatar";
import {
  Briefcase,
  Calendar,
  MapPin,
  AlertCircle,
  ChevronRight,
  Filter,
  X,
  Image as ImageIcon,
} from "lucide-react";
import { IoIosCloseCircle, IoMdCloseCircle } from "react-icons/io";
import { RiImageAddLine } from "react-icons/ri";
import {
  useUpdateRepairByIdMutation,
  useGetSchedulesByTechnicianEmailQuery,
  useGetRepairReportByIDQuery,
  useSubmitStartTimeMutation,
  useSubmitEndTimeMutation,
  useCompleteRepairReportMutation,
} from "../../slices/maintenanceApiSlice";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { maintenanceApiSlice } from "../../slices/maintenanceApiSlice";
import { useGetUserByEmailQuery } from "../../slices/userApiSlice";
import { createSelector } from "reselect";
import { toast } from "sonner";

// shadcn UI imports
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";
import { Separator } from "../../components/ui/separator";
import { formatImageUrl } from "../../lib/utils";

const WorkOrderModal = ({ order, onClose, data = [] }) => {
  const [teamMembers, setTeamMembers] = useState(order.teamMembers || []);
  const [newMember, setNewMember] = useState("");
  const [selectedWorkStatus, setSelectedWorkStatus] = useState(
    order.repairInfo.status || "",
  );
  const [images, setImages] = useState([]); // Allow multiple images
  const [imageError, setImageError] = useState("");
  const fileInputRef = useRef(null);

  const [updateRepairById] = useUpdateRepairByIdMutation();

  const repairID = order.repairInfo.repairID;
  const tm = teamMembers.join(", ");

  const {
    data: repairReport,
    isLoading: repairReportLoading,
    error: repairReportError,
    refetch,
  } = useGetRepairReportByIDQuery(repairID, {
    skip: !repairID, // skip until userID is available
  });

  const reportExists = Array.isArray(repairReport) && repairReport.length > 0;
  const [submitStartTime] = useSubmitStartTimeMutation();
  const [submitEndTime] = useSubmitEndTimeMutation();
  const [completeRepairReport, { isLoading: posting }] =
    useCompleteRepairReportMutation();

  useEffect(() => {
    if (reportExists) {
      const report = repairReport[0];

      setFormData({
        reportID: report.repairReportID ?? "",
        startTime: report.startTime ?? "",
        endTime: report.endTime ?? "",
        finishedDate: report.finishedDate ?? "",
        totalCost: report.totalCost ?? "",
        information: report.information ?? "",
        partsUsed: Array.isArray(report.partsUsed)
          ? report.partsUsed.join(", ")
          : "",
        repairID: report.repairID ?? "",
        images: report.images ?? [],
      });
    }
  }, [repairReport, reportExists]);

  const [formData, setFormData] = useState({
    reportID: "",
    startTime: "",
    endTime: "",
    finishedDate: "",
    totalCost: "",
    information: "",
    partsUsed: "",
    repairID: repairID,
    images: [],
  });

  const handleInputChange = (e, field) => {
    const value = e.target.value;
    setFormData((prevData) => ({
      ...prevData,
      [field]: value,
    }));
  };
  // Handle adding new team member
  const handleAddMember = () => {
    if (newMember.trim() && !teamMembers.includes(newMember)) {
      setTeamMembers([...teamMembers, newMember]);
      setNewMember(""); // Reset input
    }
  };

  // Handle removing team member
  const handleRemoveMember = (index) => {
    setTeamMembers(teamMembers.filter((_, i) => i !== index));
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (images.length + files.length > 5) {
      toast.warning(
        "Upload Limit Exceeded: You can upload a maximum of 5 images.",
      );

      return;
    }
    setImageError("");
    setImages((prevImages) => [...prevImages, ...files]);
  };

  const removeImage = (index) => {
    setImages(images.filter((_, i) => i !== index));
  };

  const handleAdd = async (e) => {
    e.preventDefault();

    // pull out what we need (reportID was set in your useEffect)
    const { reportID, totalCost, information, partsUsed } = formData;

    // basic validation
    if (
      !reportID ||
      !String(totalCost).trim() ||
      !String(information).trim() ||
      !String(partsUsed).trim() ||
      teamMembers.length === 0
    ) {
      toast.warning(
        "Please fill in all fields: FinishedDate, TotalCost, Information, PartsUsed, Technicians and Images",
      );
      return;
    }

    // build FormData with ONLY the fields you still need to send
    const sendData = new FormData();
    sendData.append("totalCost", String(totalCost).trim());
    sendData.append("information", String(information).trim());

    String(partsUsed)
      .split(",")
      .map((p) => p.trim())
      .filter(Boolean)
      .forEach((part) => sendData.append("partsUsed", part));

    teamMembers
      .map((tech) => tech.trim())
      .filter(Boolean)
      .forEach((tech) => sendData.append("technicians", tech));

    images.forEach((img) => sendData.append("images", img));

    try {
      // If status is being changed to Completed, perform those updates now
      if (selectedWorkStatus === "Completed") {
        await updateRepairById({
          repairID: order.repairID,
          updateFields: { status: "Completed" },
        }).unwrap();

        if (!formData.endTime) {
          const currentTime = new Date().toLocaleTimeString("en-GB", {
            hour12: false,
            hour: "2-digit",
            minute: "2-digit",
          });
          const currentDate = new Date().toISOString().split("T")[0];

          await submitEndTime({
            reportID: formData.reportID,
            endTime: currentTime,
            finishedDate: currentDate,
          }).unwrap();
        }
      }

      // call your “complete” mutation
      await completeRepairReport({
        reportID, // path param
        formData: sendData, // body
      }).unwrap();

      toast.success("Repair Report completed!");

      // reset
      setFormData((f) => ({
        ...f,
        totalCost: "",
        information: "",
        partsUsed: "",
      }));
      setTeamMembers([]);
      setImages([]);
      refetch();
    } catch (err) {
      console.error("Error completing repair report:", err);
      toast.error(err?.data?.message || "Something went wrong.");
    }
  };

  const WorkOrder = [
    { value: "Pending", label: "Pending" },
    { value: "In Progress", label: "In Progress" },
    { value: "Completed", label: "Completed" },
  ];

  const getFilteredOptions = () => {
    switch (selectedWorkStatus) {
      case "Pending":
        return WorkOrder.filter((o) => o.value !== "Completed"); // Only allow In Progress
      case "In Progress":
        return WorkOrder.filter((o) => o.value !== "Pending"); // Only allow Completed
      case "Completed":
        return WorkOrder.filter((o) => o.value === "Completed"); // Disable changing
      default:
        return WorkOrder;
    }
  };
  const today = new Date().toISOString().split("T")[0];

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl w-[calc(100vw-2rem)] sm:w-[95vw] md:w-full max-h-[90vh] overflow-y-auto p-4 sm:p-6">
        <DialogHeader className="space-y-1.5">
          <DialogTitle className="text-lg sm:text-xl">
            Work Order Details
          </DialogTitle>
        </DialogHeader>

        <form className="space-y-4 sm:space-y-6 mt-4" onSubmit={handleAdd}>
          <div className="space-y-1.5 sm:space-y-2">
            <Label htmlFor="assetName" className="text-sm font-medium">
              Asset Name:
            </Label>
            <Input
              id="assetName"
              type="text"
              value={order.repairInfo.assetName}
              readOnly
              className="h-9 sm:h-10"
            />
          </div>

          <div className="space-y-1.5 sm:space-y-2">
            <Label htmlFor="date" className="text-sm font-medium">
              Date:
            </Label>
            <Input
              id="date"
              type="text"
              value={order.reportingDate || "N/A"}
              readOnly
              className="h-9 sm:h-10"
            />
          </div>

          <div className="space-y-1.5 sm:space-y-2">
            <Label htmlFor="area" className="text-sm font-medium">
              Area:
            </Label>
            <Input
              id="area"
              type="text"
              value={order.repairInfo.area || "N/A"}
              readOnly
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description:</Label>
            <Textarea
              id="description"
              value={order.repairInfo.description || "No description"}
              readOnly
              className="min-h-[100px]"
            />
          </div>

          <div className="space-y-2">
            <Label>Asset Images:</Label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 p-4 border rounded-lg bg-gray-50">
              {Array.isArray(order.repairInfo.images) &&
              order.repairInfo.images.length > 0 ? (
                order.repairInfo.images.map((imgSrc, index) => (
                  <img
                    key={index}
                    src={imgSrc}
                    alt={`Work Order ${index + 1}`}
                    className="w-full h-32 object-cover rounded-lg border"
                  />
                ))
              ) : order.repairInfo.images ? (
                <img
                  src={order.repairInfo.images}
                  alt="Work Order"
                  className="w-full h-32 object-cover rounded-lg border"
                />
              ) : (
                <p className="text-gray-500">No image available</p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="workStatus">Work Status:</Label>
            <Select
              value={selectedWorkStatus}
              onValueChange={async (value) => {
                setSelectedWorkStatus(value);

                // If changing to Completed, we wait for the final Submit button to update the backend
                if (value === "Completed") return;

                try {
                  await updateRepairById({
                    repairID: order.repairID,
                    updateFields: { status: value },
                  }).unwrap();

                  if (value === "In Progress") {
                    const currentTime = new Date().toLocaleTimeString("en-GB", {
                      hour12: false,
                      hour: "2-digit",
                      minute: "2-digit",
                    });

                    const response = await submitStartTime({
                      repairID: order.repairID,
                      startTime: currentTime,
                    }).unwrap();

                    setFormData((prev) => ({
                      ...prev,
                      startTime: response.startTime,
                    }));
                  }

                  refetch();
                  toast.success(
                    `Work Status Updated: Status is now "${value}"`,
                  );
                } catch (err) {
                  console.error("❌ Failed to update work status:", err);
                  toast.error(
                    "Error Updating Status: Could not update status. Try again later.",
                  );
                }
              }}
            >
              <SelectTrigger className="w-full max-w-xs">
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                {getFilteredOptions().map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Time:</Label>
            <div className="flex gap-4">
              <div className="flex-1">
                <Label htmlFor="startTime" className="text-sm text-gray-600">
                  Start Time
                </Label>
                <Input
                  id="startTime"
                  type="time"
                  value={
                    reportExists && repairReport?.startTime
                      ? repairReport.startTime
                      : formData.startTime
                  }
                  readOnly
                />
              </div>
              <div className="flex-1">
                <Label htmlFor="endTime" className="text-sm text-gray-600">
                  End Time
                </Label>
                <Input
                  id="endTime"
                  type="time"
                  value={
                    reportExists && repairReport?.endTime
                      ? repairReport.endTime
                      : formData.endTime
                  }
                  readOnly={reportExists}
                />
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="finishedDate">Finished Date:</Label>
            <Input
              id="finishedDate"
              type="date"
              value={
                reportExists && repairReport?.finishedDate
                  ? repairReport.finishedDate
                  : formData.finishedDate
              }
              readOnly={reportExists}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="totalCost">Total Cost:</Label>
            <Input
              id="totalCost"
              type="text"
              placeholder="Enter Total Cost"
              value={
                reportExists && repairReport[0]?.totalCost
                  ? repairReport[0].totalCost
                  : formData.totalCost
              }
              onChange={(e) => handleInputChange(e, "totalCost")}
              readOnly={reportExists && !!repairReport[0]?.totalCost}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="partsUsed">Parts Used:</Label>
            <Textarea
              id="partsUsed"
              placeholder="Enter parts used (e.g., wood, metal, screws)"
              value={
                reportExists && repairReport[0]?.partsUsed
                  ? repairReport[0].partsUsed
                  : formData.partsUsed || ""
              }
              onChange={(e) =>
                setFormData({ ...formData, partsUsed: e.target.value })
              }
              readOnly={reportExists && !!repairReport[0]?.partsUsed}
            />
          </div>
          {reportExists && repairReport[0]?.images ? (
            <div className="space-y-2">
              <Label>Repaired Images:</Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {repairReport[0].images.map((imgSrc, index) => (
                  <div key={index} className="relative">
                    <img
                      src={imgSrc}
                      alt={`Work Order ${index + 1}`}
                      className="w-full h-32 object-cover rounded-lg border"
                    />
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <Label>Upload Images:</Label>
                <input
                  type="file"
                  accept="image/*"
                  ref={fileInputRef}
                  multiple
                  onChange={handleImageUpload}
                  style={{ display: "none" }}
                />
                {imageError && (
                  <p className="text-red-500 text-sm">{imageError}</p>
                )}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {images.map((img, idx) => (
                    <div key={idx} className="relative">
                      <img
                        className="w-full h-32 object-cover rounded-lg border"
                        src={
                          typeof img === "string"
                            ? formatImageUrl(img)
                            : URL.createObjectURL(img)
                        }
                        alt={`Preview ${idx}`}
                      />
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        className="absolute -top-2 -right-2 h-6 w-6 rounded-full p-0"
                        onClick={() => removeImage(idx)}
                      >
                        ×
                      </Button>
                    </div>
                  ))}
                  {images.length < 5 && (
                    <div
                      className="w-full h-32 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-gray-400 transition-colors"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <RiImageAddLine className="h-8 w-8 text-gray-400" />
                      <p className="text-sm text-gray-600 mt-2">
                        Upload Images
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {reportExists && repairReport[0]?.technicians ? (
            <div className="space-y-2">
              <Label>Team Members:</Label>
              <div className="flex flex-wrap gap-2">
                {repairReport[0].technicians.split(",").map((member, index) => (
                  <Badge key={index} variant="secondary">
                    {member}
                  </Badge>
                ))}
              </div>
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <Label>Team Members:</Label>
                <div className="flex gap-2">
                  <Input
                    type="text"
                    placeholder="Enter Name"
                    value={newMember}
                    onChange={(e) => setNewMember(e.target.value)}
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    onClick={handleAddMember}
                    variant="outline"
                    style={{
                      backgroundColor:
                        "rgb(48 88 69 / var(--tw-bg-opacity, 1))",
                      color: "white",
                    }}
                  >
                    Add
                  </Button>
                </div>
              </div>
              {teamMembers.length > 0 && (
                <div className="space-y-2">
                  <Label></Label>
                  <div className="flex flex-wrap gap-2">
                    {teamMembers.map((member, index) => (
                      <Badge key={index} variant="secondary" className="pr-1">
                        {member}
                        <IoMdCloseCircle
                          onClick={() => handleRemoveMember(index)}
                          className="ml-1 cursor-pointer hover:text-red-500"
                        />
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          <div className="space-y-2">
            <Label htmlFor="information">Additional Information:</Label>
            <Textarea
              id="information"
              placeholder="Enter any additional information"
              value={
                reportExists && repairReport[0]?.information
                  ? repairReport[0].information
                  : formData.information
              }
              onChange={(e) => handleInputChange(e, "information")}
              readOnly={reportExists && !!repairReport[0]?.information}
            />
          </div>

          {(!reportExists ||
            !repairReport[0]?.finishedDate ||
            !repairReport[0]?.totalCost ||
            !repairReport[0]?.information ||
            !repairReport[0]?.partsUsed ||
            !repairReport[0]?.images?.length ||
            !repairReport[0]?.technicians) && (
            <div className="flex justify-end">
              <Button
                type="submit"
                disabled={posting}
                className="min-w-[100px]"
                style={{
                  backgroundColor: "rgb(48 88 69 / var(--tw-bg-opacity, 1))",
                  color: "white",
                }}
              >
                {posting ? "Posting..." : "Done"}
              </Button>
            </div>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
};

const TechnicianHome = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPriority, setSelectedPriority] = useState("all");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedWorkStatus, setSelectedWorkStatus] = useState("");

  const selectUserInfo = (state) => state.auth.userInfo || {};
  const getUserEmail = createSelector(selectUserInfo, (userInfo) => {
    const email = userInfo?.username || userInfo?.email || "";
    return email === "null" ? "" : email;
  });
  const [data, setData] = useState([]);
  const email = useSelector(getUserEmail);
  const { data: userData } = useGetUserByEmailQuery(email, {
    skip: !email,
  });

  const userEmail = userData?.user?.email || email;
  const { data: technicianSchedules, isLoading } =
    useGetSchedulesByTechnicianEmailQuery(userEmail, {
      skip: !userEmail,
    });
  const dispatch = useDispatch();

  const name = userData?.user?.name;

  useEffect(() => {
    if (isLoading) {
      toast.loading("Loading work order...", { id: "loading-work-order" });
    } else {
      toast.dismiss("loading-work-order");
    }
  }, [isLoading]);

  useEffect(() => {
    // Show "No work orders" alert when data is loaded but empty
    if (
      !isLoading &&
      (!technicianSchedules || technicianSchedules.length === 0)
    ) {
      toast.info("No Work Orders: You currently have no assigned work orders.");
    }
  }, [isLoading, technicianSchedules]);

  useEffect(() => {
    const fetchRepairDetails = async () => {
      if (technicianSchedules && technicianSchedules.length) {
        const repairPromises = technicianSchedules.map(async (schedule) => {
          try {
            const repair = await dispatch(
              maintenanceApiSlice.endpoints.getRepairById.initiate(
                schedule?.repairID,
                {
                  forceRefetch: true,
                },
              ),
            ).unwrap();

            return {
              ...schedule,
              repairInfo: repair, // attach the repair detail here
            };
          } catch (err) {
            console.error(
              `❌ Error fetching repair for ID ${schedule?.repairID}:`,
              err,
            );
            return null; // return null to filter out later
          }
        });

        const results = await Promise.all(repairPromises);
        const validMergedData = results.filter(Boolean); // filter out failed ones
        setData(validMergedData); // ⬅️ final combined array
      }
    };

    fetchRepairDetails();
  }, [technicianSchedules, dispatch]);

  const uniquePriorities = Array.from(
    new Set(
      data
        .map((item) => item.repairInfo.priority?.toLowerCase())
        .filter(Boolean),
    ),
  ).map((priority) => ({
    value: priority,
    label: priority ? priority.charAt(0).toUpperCase() + priority.slice(1) : "",
  }));

  const searchRecursively = (obj, searchTerm) => {
    if (typeof obj !== "object" || obj === null) {
      return (
        obj &&
        obj
          .toString()
          .toLowerCase()
          .includes(searchTerm?.toLowerCase() || "")
      );
    }

    // Check all values in the object (including nested objects)
    return Object.values(obj).some((value) =>
      searchRecursively(value, searchTerm),
    );
  };

  const filteredData =
    data && data.length
      ? data.filter((item) => {
          // Match search term with any field at any level in the object
          const matchesSearch = searchRecursively(item, searchTerm);

          const matchesPriority =
            selectedPriority === "all" ||
            selectedPriority === "" ||
            item.repairInfo.priority?.toLowerCase() ===
              selectedPriority.toLowerCase();

          const matchesWorkStatus =
            selectedWorkStatus === "" ||
            item.repairInfo.status?.toLowerCase() ===
              selectedWorkStatus.toLowerCase();

          return matchesSearch && matchesPriority && matchesWorkStatus;
        })
      : [];

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 18) return "Good afternoon";
    return "Good evening";
  };

  const getFormattedDate = () => {
    const today = new Date();
    const options = { weekday: "long", month: "long", day: "numeric" };
    return today.toLocaleDateString("en-US", options);
  };

  const greeting = getGreeting();
  const formattedDate = getFormattedDate();

  const userProfileImage = userData?.user?.image || null;

  return (
    <div className="p-3 sm:p-4 md:py-6 md:px-8 space-y-4 sm:space-y-6">
      {/* My Work Orders Table */}
      <div className="bg-white overflow-hidden">
        {/* Table Header */}
        <div className="border-b border-zinc-200 bg-white pb-4 sm:pb-6 px-1">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 sm:gap-6">
            <div className="flex items-center gap-3 sm:gap-4">
              <Avatar
                src={userProfileImage}
                alt={name || "User"}
                size={64}
                className="h-14 w-14 sm:h-16 sm:w-16 md:h-20 md:w-20 ring-4 ring-zinc-50 flex-shrink-0"
              />
              <div className="space-y-0.5 sm:space-y-1 min-w-0">
                <p className="text-[10px] sm:text-xs font-bold text-zinc-400 uppercase tracking-widest">
                  {formattedDate}
                </p>
                <h2 className="text-lg sm:text-xl md:text-2xl font-black text-zinc-900 tracking-tight truncate">
                  {greeting},{" "}
                  <span className="text-zinc-500 font-light">{name}</span>
                </h2>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
              <Select
                value={selectedPriority}
                onValueChange={(value) => setSelectedPriority(value)}
              >
                <SelectTrigger className="w-full sm:w-48 h-10 border-zinc-200 bg-zinc-50/50 rounded-xl">
                  <SelectValue placeholder="All Priorities" />
                </SelectTrigger>
                <SelectContent className="rounded-xl shadow-xl">
                  <SelectItem value="all">All Priorities</SelectItem>
                  {uniquePriorities.map((priority) => (
                    <SelectItem key={priority.value} value={priority.value}>
                      {priority.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Link
                to="work-order"
                className="flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 bg-zinc-900 text-white rounded-xl hover:bg-zinc-800 transition-all active:scale-95 text-sm font-bold shadow-lg shadow-zinc-200"
                style={{
                  backgroundColor: "rgb(48 88 69 / var(--tw-bg-opacity, 1))",
                  color: "white",
                }}
              >
                View all work
                <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>

        {/* Table Content */}
        <div className="py-3 sm:py-4">
          <p className="text-[10px] sm:text-xs font-bold text-zinc-400 uppercase tracking-tighter px-1 py-3 sm:py-4 flex items-center gap-2">
            <span className="h-1 w-1 rounded-full bg-zinc-400" />
            {filteredData.length}{" "}
            {filteredData.length === 1 ? "work order" : "work orders"} pending
            assignment
          </p>
          {filteredData.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {filteredData.map((order) => (
                <WorkOrderCard
                  key={order.scheduleID}
                  {...order}
                  onView={() => setSelectedOrder(order)}
                />
              ))}
            </div>
          ) : (
            <div className="py-8 sm:py-12 text-center px-4">
              <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-zinc-100 flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <Briefcase className="w-6 h-6 sm:w-8 sm:h-8 text-zinc-400" />
              </div>
              <h3 className="text-base sm:text-lg font-medium text-zinc-900 mb-2">
                No work orders
              </h3>
              <p className="text-sm text-zinc-600">
                You don't have any pending work orders at the moment.
              </p>
            </div>
          )}
        </div>
      </div>

      {selectedOrder && (
        <WorkOrderModal
          order={selectedOrder}
          onClose={() => setSelectedOrder(null)}
          data={data}
        />
      )}
    </div>
  );
};

export default TechnicianHome;
